import { FC, useEffect, useRef, useState } from 'react';
import { Svg } from '@hopskipdrive/react-components';
import { differenceWith, filter, find, isEqual, unionBy } from 'lodash';

import { useAuth } from '../../../contexts/AuthContext';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { isSupervisor } from '../Alerts.helpers';
import { SupervisorParams, UserParams } from '../types';

import { Banner } from './styles';

interface RefreshBannerProps {
  employees: Employee[];
  currentAlerts: Alerts[];
  queryParams: UserParams | SupervisorParams;
  onRefresh: () => void;
  onVisible: () => void;
}

export const RefreshBanner: FC<RefreshBannerProps> = ({
  employees,
  currentAlerts,
  queryParams,
  onRefresh,
  onVisible,
}) => {
  const { user } = useAuth();
  const [newAlertCount, setNewAlertCount] = useState(0);
  const [highPriorityAlertCount, setHighPriorityAlertCount] = useState(0);
  const [alertsSinceLastRefresh, setAlertsSinceLastRefresh] = useState<Alerts[]>([]);
  const incidentTopic = 'incidents';
  const etaChangeTopic = 'trip_etas';
  const { lastJsonMessage: alertsStateChange } = useWebSocket({ topic: incidentTopic });
  const { lastJsonMessage: alertsEtaStateChange } = useWebSocket({ topic: etaChangeTopic });
  const bannerText = `Alert updates available${
    highPriorityAlertCount > 0 ? ' (some high-priority)' : ''
  }, click to refresh`;
  const supervisorParams = isSupervisor(user) ? (queryParams as SupervisorParams) : null;

  const stateRef = useRef({ newAlertCount, highPriorityAlertCount, alertsSinceLastRefresh });

  useEffect(() => {
    stateRef.current = { newAlertCount, highPriorityAlertCount, alertsSinceLastRefresh };
  });

  useEffect(() => {
    if (alertsStateChange?.data && alertsStateChange.topic === incidentTopic) {
      alertsStateChange.data.forEach((data) => {
        handleIncidentChange(data);
      });
    }
  }, [alertsStateChange]);

  useEffect(() => {
    if (alertsEtaStateChange?.data && alertsEtaStateChange.topic === etaChangeTopic) {
      handleIncidentETAChange(alertsEtaStateChange.data);
    }
  }, [alertsEtaStateChange]);

  useEffect(() => {
    const alertCount = differenceWith(alertsSinceLastRefresh, currentAlerts, isEqual).length;

    if (stateRef.current.newAlertCount === 0 && alertCount !== 0) {
      setNewAlertCount(alertCount);
    }
    setHighPriorityAlertCount(filter(alertsSinceLastRefresh, { highPriority: true }).length);
  }, [alertsSinceLastRefresh]);

  useEffect(() => {
    newAlertCount && onVisible();
  }, [newAlertCount, onVisible]);

  const handleIncidentChange = (data) => {
    if (!data?.incidents) return;

    const { newAlertCount: currentNewAlertCount, highPriorityAlertCount: currentHighPriority } = stateRef.current;
    const incidentTypesSelected = queryParams.incident_types ? queryParams.incident_types : [];
    let employeesSelected;

    if (isSupervisor(user)) {
      employeesSelected = filter(employees, (employee) => {
        return supervisorParams?.assignees && supervisorParams?.assignees?.indexOf(employee.id.toString()) >= 0;
      });
    }

    if (currentNewAlertCount > 0 && currentHighPriority > 0) {
      return;
    }

    // Functional updater so multiple batches in a single WebSocket message accumulate
    // instead of overwriting each other when React 18 batches the setStates.
    setAlertsSinceLastRefresh((prev) => {
      if (isSupervisor(user)) {
        return unionBy(data.incidents, prev, 'tripSlug').filter(
          (alert) =>
            alert.state !== 'resolved' &&
            (employeesSelected.length === 0 ||
              find(employeesSelected, (employee: Employee) => {
                return employee.displayName === alert.assigneeName;
              })) &&
            (incidentTypesSelected.length === 0 || incidentTypesSelected.indexOf(alert.incidentType) !== -1)
        );
      }

      return unionBy(data.incidents, prev, 'tripSlug').filter((alert) => {
        return (
          alert.state !== 'resolved' &&
          alert.assigneeName === user?.displayName &&
          (incidentTypesSelected.length === 0 || incidentTypesSelected.indexOf(alert.incidentType) !== -1)
        );
      });
    });
  };

  const handleIncidentETAChange = (data) => {
    if (stateRef.current.newAlertCount > 0) {
      return;
    }
    const updatedAlerts = currentAlerts.map((alert) => {
      const foundAlert = data.find((newAlert) => newAlert.tripSlug === alert.tripSlug);

      return {
        ...alert,
        ...(foundAlert && { tripETA: foundAlert.tripETA }),
      };
    });

    setAlertsSinceLastRefresh(updatedAlerts);
  };

  return newAlertCount > 0 ? (
    <Banner
      data-testid="refresh-banner"
      $hasHighPriority={highPriorityAlertCount > 0}
      onClick={() => {
        setNewAlertCount(0);
        setHighPriorityAlertCount(0);
        onRefresh();
      }}
    >
      <Svg icon="info" width={32} />
      {bannerText}
    </Banner>
  ) : null;
};
